// Fits the board and keyboard to the screen: tiles shrink on narrow or short phones so the whole
// game fits without scrolling, and grow on desktop.
import { useEffect, useState } from 'react'

export const DESKTOP_MIN = 1100
/**
 * From this width the game screens get their side panels (330 + 560 + 350 px plus gaps and a
 * scrollbar); between DESKTOP_MIN and this they keep the desktop header but play in one column.
 */
export const SIDE_PANELS_MIN = 1400

export interface Fit {
  desktop: boolean
  /** Wide enough for the side panels (SIDE_PANELS_MIN). */
  wide: boolean
  tile: number
  keyW: number
  keyH: number
  keyGap: number
  keyWide: number
}

/** `extra`: px of other things on the phone game screen (the challenge bar), taken from the board. */
function compute(extra: number): Fit {
  const vw = window.innerWidth
  const vh = window.innerHeight
  if (vw >= DESKTOP_MIN) {
    const keyH = vh < 860 ? 46 : 52
    const tile = Math.max(40, Math.min(60, Math.floor((vh - 68 - 64 - 34 - 46 - (3 * keyH + 16) - 130) / 6.2)))
    return { desktop: true, wide: vw >= SIDE_PANELS_MIN, tile, keyW: 42, keyH, keyGap: 6, keyWide: 66 }
  }
  const w = Math.min(vw, 480)
  const keyH = vh < 720 ? 46 : vh < 800 ? 50 : 54
  const keyGap = w < 370 ? 4 : 5
  const keyW = Math.min(36, Math.floor((w - 12 - 9 * keyGap) / 10))
  const byWidth = (w - 24 - 102) / 5
  const byHeight = (vh - extra - 56 - 58 - 60 - 28 - 42 - (3 * keyH + 16) - 26 - 30) / 6
  const tile = Math.max(30, Math.min(56, Math.floor(Math.min(byWidth, byHeight))))
  return { desktop: false, wide: false, tile, keyW, keyH, keyGap, keyWide: Math.round(keyW * 1.6) }
}

export function useFit(extra = 0): Fit {
  const [fit, setFit] = useState(() => compute(extra))
  useEffect(() => {
    const on = () => setFit(compute(extra))
    on()
    window.addEventListener('resize', on)
    return () => window.removeEventListener('resize', on)
  }, [extra])
  return fit
}

export function useIsDesktop(): boolean {
  const [d, setD] = useState(() => window.innerWidth >= DESKTOP_MIN)
  useEffect(() => {
    const on = () => setD(window.innerWidth >= DESKTOP_MIN)
    window.addEventListener('resize', on)
    return () => window.removeEventListener('resize', on)
  }, [])
  return d
}
